import { api, apiUrl, resolveTenantSlug, tokens } from './api';

export interface CertificateTypeInfo {
  type: string;
  label: string;
  serialPrefix: string;
  fields: string[];
}

export interface Certificate {
  id: string;
  type: string;
  status: string;            // Issued | Revoked
  serialNumber: string;
  studentId: string;
  studentName: string;
  fields: Record<string, string>;
  issuedByUserId: string;
  issuedAt: string;
  revokedAt: string | null;
  revokeReason: string | null;
  hasDocument: boolean;
}

export interface PagedCertificates {
  items: Certificate[];
  total: number;
  skip: number;
  take: number;
}

export const certificatesApi = {
  catalogue: () => api.get<CertificateTypeInfo[]>('/api/certificates/catalogue'),

  list: (opts?: { studentId?: string; type?: string; status?: string; skip?: number; take?: number; search?: string }) => {
    const q = new URLSearchParams();
    if (opts?.studentId) q.set('studentId', opts.studentId);
    if (opts?.type) q.set('type', opts.type);
    if (opts?.status) q.set('status', opts.status);
    if (opts?.skip != null) q.set('skip', String(opts.skip));
    if (opts?.take != null) q.set('take', String(opts.take));
    if (opts?.search) q.set('search', opts.search);
    const qs = q.toString();
    return api.get<PagedCertificates>(`/api/certificates/${qs ? `?${qs}` : ''}`);
  },

  get: (id: string) => api.get<Certificate>(`/api/certificates/${id}`),
  issue: (b: { type: string; studentId: string; fields: Record<string, string> }) =>
    api.post<Certificate>('/api/certificates/', b),
  revoke: (id: string, reason: string) =>
    api.post<Certificate>(`/api/certificates/${id}/revoke`, { reason }),

  // Open the print-ready certificate. The endpoint is auth-gated, so we fetch it with the bearer +
  // tenant headers and open the returned HTML as a blob in a new tab (where "Print / Save as PDF"
  // lives). A plain link wouldn't carry the token.
  openDocument: async (id: string) => {
    const slug = resolveTenantSlug();
    const res = await fetch(apiUrl(`/api/certificates/${id}/document`), {
      headers: {
        ...(slug ? { 'X-Tenant': slug } : {}),
        ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
      },
    });
    if (!res.ok) throw new Error('Could not load the certificate.');
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

// A human label for a field key ("dateOfBirth" → "Date of birth").
export function fieldLabel(key: string): string {
  const spaced = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  return spaced.trim();
}
